import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ArrowUpRight } from "lucide-react";
import { useSeo } from "@/lib/seo";

const MARKETPLACE_CARDS = [
  {
    id: "browse-golf-shop",
    eyebrow: "Featured",
    title: "Browse Golf Shop",
    description: "Explore featured golf products and beginner essentials.",
    href: "https://connect.exono.me/IM/7868",
  },
  {
    id: "shop-used-gear",
    eyebrow: "Pre-Owned",
    title: "Shop Used Golf Gear",
    description: "Browse quality used golf clubs, bags, and accessories.",
    href: "https://connect.exono.me/IM/7867",
  },
  {
    id: "buy-balls-clubs",
    eyebrow: "Essentials",
    title: "Buy Golf Balls & Clubs",
    description: "Find affordable golf balls and equipment for all skill levels.",
    href: "https://connect.exono.me/IM/7866",
  },
];

export default function Shop() {
  useSeo();
  return (
    <div className="min-h-screen bg-cream" data-testid="shop-page">
      <Header />

      {/* Hero */}
      <section className="relative border-b border-cream-border">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <div className="eyebrow mb-6">The Golf Market — Pre-Owned & Curated</div>
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-forest">
                Quiet luxury,
                <br />
                <span className="italic text-gold">second innings.</span>
              </h1>
              <p className="mt-8 max-w-xl font-sans text-base md:text-lg leading-relaxed text-forest-muted">
                Three hand-picked storefronts on Exonome — featured gear, pre-owned clubs and
                everyday essentials. One tap, secure checkout, all under the Golf Start standard.
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <div className="rule md:ml-auto mb-4" />
              <div className="eyebrow">Powered by</div>
              <div className="mt-2 font-serif text-xl text-forest-dark">
                Exonome &middot; connect.exono.me
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marketplace shortcuts — Exonome */}
      <section
        data-testid="marketplace-cards-section"
        className="border-b border-cream-border bg-cream-light"
      >
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-16 md:py-24">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <div className="eyebrow">Shop the Marketplace</div>
              <h2 className="mt-4 font-serif text-3xl md:text-5xl text-forest leading-[1.05]">
                Three shelves, <em className="italic text-gold font-light">one tap away.</em>
              </h2>
            </div>
            <p className="max-w-md font-sans text-sm md:text-base text-forest-muted leading-relaxed">
              Hand-picked storefronts on Exonome — secure checkout, fast handover, all under
              the Golf Start standard.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {MARKETPLACE_CARDS.map((card, i) => (
              <a
                key={card.id}
                href={card.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`marketplace-card-${card.id}`}
                className="group relative flex flex-col bg-cream border border-cream-border p-8 md:p-10 transition-transform duration-500 ease-out hover:-translate-y-1 hover:border-gold"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="font-serif text-5xl text-gold leading-none">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <ArrowUpRight
                    className="h-5 w-5 text-forest-muted transition-colors group-hover:text-gold"
                    strokeWidth={1.5}
                  />
                </div>

                <div className="mt-10">
                  <div className="eyebrow">{card.eyebrow}</div>
                  <h3 className="mt-3 font-serif text-3xl md:text-[2rem] leading-tight text-forest">
                    {card.title}
                  </h3>
                  <p className="mt-4 font-sans text-sm md:text-base leading-relaxed text-forest-muted">
                    {card.description}
                  </p>
                </div>

                <div className="mt-10 pt-6 border-t border-cream-border flex items-center justify-between">
                  <span className="font-sans text-xs tracking-[0.2em] uppercase text-forest-dark group-hover:text-gold transition-colors">
                    Open marketplace
                  </span>
                  <span className="font-sans text-[10px] tracking-[0.25em] uppercase text-forest-muted">
                    connect.exono.me
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
